import common from '../common.js';

// Bridges the working dispatch to a new browser tab (which boots a fresh,
// empty Vuex store) so the map page can still consume the selected sites.
const MAP_CACHE_KEY = 'dispatch-map-payload';

// The save response is the saved dispatch DTO (PascalCase) plus CreatedBy.
function extractDispatchId(resp) {
  if (resp == null) return null;
  return resp.DispatchId ?? resp.dispatchId ?? null;
}

// Drop the map tab's cached copy, but only when it is the dispatch being
// deleted — another dispatch may legitimately be cached for an open map tab.
function clearCachedDispatch(id) {
  try {
    const raw = localStorage.getItem(MAP_CACHE_KEY);
    if (!raw) return;
    const cached = JSON.parse(raw);
    if (cached && id != null && String(cached.dispatchId) !== String(id)) return;
    localStorage.removeItem(MAP_CACHE_KEY);
  } catch (error) {
    console.warn('clearCachedDispatch failed.', error);
  }
}

function parseJson(str, fallback) {
  if (str == null || str === '') return fallback;
  if (typeof str !== 'string') return str;
  try {
    const v = JSON.parse(str);
    return v == null ? fallback : v;
  } catch (error) {
    return fallback;
  }
}

// Supply has its own SupplyParams column. Before it existed the tote figures
// rode inside DeliveryParams, so anything saved in that window is still read
// here -- delete the fallback (and the `delete` below) once no stored dispatch
// carries the nested form.
function readSupply(dto, deliveryParsed) {
  const own = parseJson(dto.SupplyParams ?? dto.supplyParams, null);
  if (own && typeof own === 'object') return own;
  const nested = deliveryParsed && deliveryParsed.supply;
  return nested && typeof nested === 'object' ? nested : {};
}

// Turn the API dispatch DTO (PascalCase; params/sites are JSON strings) into the
// internal shape used by the store and UI.
function normalizeDispatch(dto) {
  const parameters = parseJson(dto.DispatchParams ?? dto.dispatchParams, {});
  const rawSites = parseJson(dto.ContainerParams ?? dto.containerParams, []);
  const selectedSites = Array.isArray(rawSites) ? rawSites : [];
  const deliveryParams = parseJson(dto.DeliveryParams ?? dto.deliveryParams, {}) || {};
  const supply = readSupply(dto, deliveryParams);
  // A legacy nested copy must not leak back into deliveryParams, or the map
  // would carry it around and re-save it.
  delete deliveryParams.supply;
  return {
    dispatchId: dto.DispatchId ?? dto.dispatchId ?? null,
    name: dto.DispatchName ?? dto.dispatchName ?? dto.name ?? '',
    dispatchDate: dto.DispatchDate ?? dto.dispatchDate ?? parameters.dispatchDate ?? null,
    createdBy: dto.CreatedBy ?? dto.createdBy ?? '',
    parameters,
    deliveryParams,
    supply,
    selectedSites,
    siteCount: selectedSites.length,
  };
}

// The backend DTO for both POST (create) and PUT (update). Parameters, the
// selected-site list, the delivery/fleet settings and the supply figures all
// travel as JSON strings.
function toDispatchDto(dispatch) {
  const params = dispatch.parameters || {};

  // DispatchDate is a required DateTime; default to now when unset.
  const parsed = params.dispatchDate ? new Date(params.dispatchDate) : new Date();
  const dispatchDate = isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();

  return {
    DispatchName: dispatch.name || '',
    DispatchDate: dispatchDate,
    DispatchParams: JSON.stringify(params),
    ContainerParams: JSON.stringify(dispatch.selectedSites || []),
    DeliveryParams: JSON.stringify(dispatch.deliveryParams || {}),
    SupplyParams: JSON.stringify(dispatch.supply || {}),
  };
}

export default {
  async loadContainersStatus(context, payload) {
    const typeId = (payload && payload.typeId) || 1;
    await Promise.all([
      context.dispatch('containers/loadContainersStatusV2', { typeId }, { root: true }),
      context.dispatch('properties/loadProperties', {}, { root: true }),
    ]);
  },

  // GET /dispatch → the user's past dispatches, normalized for the UI. On
  // failure the list is left empty (Recent shows its empty state).
  async loadSavedDispatches(context, payload) {
    try {
      const responseJson = await common.getRequest(context, payload, 'dispatch');
      const list = Array.isArray(responseJson) ? responseJson.map(normalizeDispatch) : [];
      context.commit('setSavedDispatches', list);
      return list;
    } catch (error) {
      console.warn('loadSavedDispatches: GET /dispatch failed.', error);
      context.commit('setSavedDispatches', []);
      return [];
    }
  },

  startNewDispatch(context) {
    context.commit('resetCurrentDispatch');
  },

  loadDispatch(context, payload) {
    // payload is a normalized saved dispatch (parameters + selectedSites).
    const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);
    const p = payload.parameters || {};
    context.commit('setCurrentDispatch', {
      dispatchId: payload.dispatchId ?? null,
      name: payload.name || '',
      parameters: {
        dispatchDate: p.dispatchDate ?? payload.dispatchDate ?? null,
        // Dispatches saved before the method existed were all bulk runs.
        deliveryMethod: p.deliveryMethod === 'totes' ? 'totes' : 'tanker',
        // Product became a multi-select. Older dispatches stored a single
        // string, and an "all products" dispatch stored a null product with the
        // flag set — both normalise to the array form without losing meaning.
        product: p.allProducts ? [] : toArray(p.product),
        operator: toArray(p.operator),
        area: toArray(p.area),
        maxDte: p.maxDte ?? null,
        minUllage: p.minUllage ?? null,
        maxFillPct: p.maxFillPct ?? 95,
      },
      selectedSites: payload.selectedSites || [],
      supply: payload.supply || {},
      deliveryParams: payload.deliveryParams || {},
    });
  },

  // Make the dispatch with `id` the current dispatch. Uses the store if it
  // already holds it, otherwise fetches the list and loads it. Resolves to
  // whether the dispatch was found.
  async loadDispatchById(context, id) {
    const wanted = String(id);
    const current = context.getters.getCurrentDispatch;
    if (
      current &&
      String(current.dispatchId) === wanted &&
      current.selectedSites &&
      current.selectedSites.length
    ) {
      return true;
    }
    let saved = context.getters.getSavedDispatches;
    if (!saved || !saved.length) {
      saved = await context.dispatch('loadSavedDispatches');
    }
    const match = (saved || []).find((d) => String(d.dispatchId) === wanted);
    if (!match) return false;
    context.dispatch('loadDispatch', match);
    return true;
  },

  setParameters(context, payload) {
    context.commit('setCurrentDispatchParameters', payload);
  },

  setSelectedSites(context, payload) {
    context.commit('setCurrentDispatchSites', payload);
  },

  // Stash the working dispatch so a newly-opened tab can pick it up.
  cacheCurrentDispatch(context) {
    try {
      localStorage.setItem(
        MAP_CACHE_KEY,
        JSON.stringify(context.getters.getCurrentDispatch)
      );
    } catch (error) {
      console.warn('cacheCurrentDispatch failed.', error);
    }
  },

  // Rehydrate the working dispatch from cache when the in-memory one is empty
  // (e.g. the map opened in its own tab with a fresh store).
  restoreCachedDispatch(context) {
    const current = context.getters.getCurrentDispatch;
    if (current && current.selectedSites && current.selectedSites.length) return;
    try {
      const raw = localStorage.getItem(MAP_CACHE_KEY);
      if (raw) {
        context.commit('setCurrentDispatch', JSON.parse(raw));
      }
    } catch (error) {
      console.warn('restoreCachedDispatch failed.', error);
    }
  },

  // Sends the dispatch email. Attempts the API and falls back to a simulated
  // send so the flow completes before the backend endpoint exists.
  async sendDispatchEmail(context, payload) {
    try {
      return await common.postRequest(context, payload, 'dispatch/email');
    } catch (error) {
      console.warn('sendDispatchEmail: API unavailable, simulating send.', error);
      return { simulated: true, to: payload && payload.to, sentAt: new Date().toISOString() };
    }
  },

  // Stores the delivery/fleet settings from the dispatch map so they survive a
  // save and come back when the dispatch is reopened.
  setSupply(context, payload) {
    context.commit('setCurrentDispatchSupply', payload);
  },

  setDeliveryParams(context, payload) {
    context.commit('setCurrentDispatchDeliveryParams', payload);
  },

  // Persists the dispatch via POST /dispatch. Maps the working dispatch to the
  // backend DTO: parameters and the selected-site list are sent as JSON strings.
  async saveDispatch(context, payload) {
    const dispatch = payload || context.getters.getCurrentDispatch;
    const dto = toDispatchDto(dispatch);
    // Only send an id when updating; a new dispatch lets the API assign one.
    if (dispatch.dispatchId) {
      dto.DispatchId = dispatch.dispatchId;
    }

    const responseJson = await common.postRequest(context, dto, 'dispatch');

    // Hold onto the (new) id so subsequent saves update this record instead of
    // creating another. Response shape isn't finalized, so extract defensively.
    const newId = extractDispatchId(responseJson);
    if (newId != null) {
      context.commit('setCurrentDispatchId', newId);
    }

    await context.dispatch('loadSavedDispatches');
    return responseJson;
  },

  // Deletes a saved dispatch via DELETE /dispatch/{id}.
  //
  // If the deleted record is the one open in the builder, the working copy is
  // reset as well. Leaving it would keep a stale `dispatchId` on a dispatch the
  // API no longer has, and the next Save would call updateDispatch -> which
  // PUTs that id and, on failure, falls through to saveDispatch — re-creating
  // the record the user just deleted.
  async deleteDispatch(context, payload) {
    const id =
      payload != null && typeof payload === 'object' ? payload.dispatchId : payload;
    if (id == null || id === '') {
      throw new Error('No dispatch id to delete.');
    }

    await common.deleteRequest(context, `dispatch/${id}`);

    const current = context.getters.getCurrentDispatch;
    if (current && String(current.dispatchId) === String(id)) {
      context.commit('resetCurrentDispatch');
    }
    clearCachedDispatch(id);

    await context.dispatch('loadSavedDispatches');
    return id;
  },

  // Updates an existing dispatch in place via PUT /dispatch. The record is
  // identified by DispatchId in the body, not by the route. An unsaved dispatch
  // (no id) falls through to saveDispatch so the button always does the sensible
  // thing.
  async updateDispatch(context, payload) {
    const dispatch = payload || context.getters.getCurrentDispatch;
    const id = dispatch.dispatchId;
    if (id == null || id === '') {
      return context.dispatch('saveDispatch', dispatch);
    }

    const dto = { ...toDispatchDto(dispatch), DispatchId: id };
    const responseJson = await common.putRequest(context, dto, 'dispatch');

    // Keep the cached copy (used by the map tab) and the Recent list in step.
    context.dispatch('cacheCurrentDispatch');
    await context.dispatch('loadSavedDispatches');
    return responseJson;
  },
};
